import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../components/ConfirmDialog";
import StarRating from "../components/StarRating";

export default function ProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFav, setIsFav] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [canReview, setCanReview] = useState({ can_review: false, reason: "" });
  const [showForm, setShowForm] = useState(false);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState("");

  const loadAll = async () => {
    try {
      const [p, favs, rv, cr] = await Promise.all([
        api.get(`/profiles/${id}`),
        api.get("/favorites").catch(() => ({ data: [] })),
        api.get(`/profiles/${id}/reviews`),
        api.get(`/profiles/${id}/reviews/can-review`).catch(() => ({ data: { can_review: false } })),
      ]);
      setProfile(p.data);
      setIsFav(favs.data.some((x) => x.id === id));
      setReviews(rv.data);
      setCanReview(cr.data);
      if (cr.data?.existing) {
        setStars(cr.data.existing.stars);
        setComment(cr.data.existing.comment || "");
      }
    } catch (_) {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const toggleFav = async () => {
    if (isFav) {
      await api.delete(`/favorites/${id}`);
      setIsFav(false);
    } else {
      await api.post("/favorites", { profile_id: id });
      setIsFav(true);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    setReviewError("");
    setSubmitting(true);
    try {
      await api.post(`/profiles/${id}/reviews`, { stars, comment });
      setShowForm(false);
      await loadAll();
    } catch (err) {
      setReviewError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar la reseña");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteOwnReview = async () => {
    const ok = await confirm({ title: "¿Eliminar tu reseña?", confirmLabel: "Eliminar" });
    if (!ok) return;
    const mine = reviews.find((r) => r.author_id === user?.id);
    if (!mine) return;
    try {
      await api.delete(`/reviews/${mine.id}`);
      await loadAll();
      setStars(5);
      setComment("");
    } catch (err) {
      setReviewError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  if (loading) return <div className="text-center py-16 text-slate-500">Cargando…</div>;
  if (!profile) return <div className="text-center py-16 text-slate-500">Perfil no encontrado</div>;

  const isProf = profile.type === "profesor";
  const accent = isProf ? "teal" : "blue";
  const photo = profile.photo_url || `https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(profile.name)}`;
  const myReview = reviews.find((r) => r.author_id === user?.id);
  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return ""; }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Link to={`/profiles/${profile.type}`} className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="back-to-list-btn">
        <i className="fa-solid fa-arrow-left"></i> Volver
      </Link>

      <div className="kibli-card overflow-hidden">
        <div className="aspect-[16/9] md:aspect-[21/9] bg-slate-100 overflow-hidden">
          <img src={photo} alt={profile.name} className="w-full h-full object-cover" />
        </div>
        <div className="p-6 md:p-8 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-extrabold" data-testid="profile-detail-name">{profile.name}</h1>
              <div className="mt-2 text-slate-600 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1">
                  <StarRating value={profile.rating || 0} readOnly size="text-sm" testIdPrefix="profile-rating-star" />
                  <span className="font-semibold ml-1">{Number(profile.rating || 0).toFixed(1)}</span>
                  <span className="text-slate-400 text-sm">({profile.review_count || 0} {profile.review_count === 1 ? "reseña" : "reseñas"})</span>
                </span>
                <span className="flex items-center gap-1"><i className="fa-solid fa-location-dot text-slate-400"></i>{(profile.zonas || []).join(", ") || profile.ciudad}, {profile.ciudad}</span>
                <span className="flex items-center gap-1"><i className="fa-solid fa-circle-check text-green-500"></i>Cuenta verificada</span>
              </div>
            </div>
            <button onClick={toggleFav} data-testid="fav-detail-btn"
              className={`w-11 h-11 rounded-full flex items-center justify-center transition ${isFav ? "bg-red-500 text-white" : "bg-slate-100 text-slate-500 hover:text-red-500"}`}>
              <i className={`${isFav ? "fa-solid" : "fa-regular"} fa-heart`}></i>
            </button>
          </div>

          <div className={`text-3xl font-extrabold ${accent === "teal" ? "text-teal-600" : "text-blue-600"}`} data-testid="profile-detail-price">
            {profile.price_per_hour}€/h
          </div>

          {profile.bio && <p className="text-slate-600">{profile.bio}</p>}

          <div className="grid sm:grid-cols-2 gap-3">
            <Section title="Disponibilidad" icon="fa-clock">
              {(profile.availability || []).map((a) => (
                <span key={a} className="kibli-pill text-xs">{a.replace(/_/g, " ")}</span>
              ))}
              {!(profile.availability || []).length && <span className="text-slate-400 text-sm">No especificado</span>}
            </Section>
            <Section title="Idiomas" icon="fa-language">
              {(profile.languages || []).map((l) => (
                <span key={l} className="kibli-pill text-xs">{l}</span>
              ))}
              {!(profile.languages || []).length && <span className="text-slate-400 text-sm">No especificado</span>}
            </Section>
          </div>

          {isProf && profile.subjects && Object.keys(profile.subjects).length > 0 && (
            <Section title="Asignaturas" icon="fa-book">
              {Object.entries(profile.subjects).map(([cat, list]) => (
                <div key={cat} className="w-full">
                  <div className="text-sm font-semibold text-slate-700 mb-1">{cat}</div>
                  <div className="flex flex-wrap gap-2">
                    {list.map((s) => <span key={s} className="kibli-pill text-xs">{s}</span>)}
                  </div>
                </div>
              ))}
            </Section>
          )}

          <button
            onClick={() => navigate(`/messages/with/${profile.owner_id}`)}
            data-testid="profile-detail-contact-btn"
            className={`w-full py-4 ${accent === "teal" ? "btn-primary-teal" : "btn-primary"}`}
          >
            <i className="fa-solid fa-comments"></i> Contactar a {profile.name.split(" ")[0]}
          </button>
        </div>
      </div>

      {/* ----------- Reviews section ----------- */}
      <div className="kibli-card p-6 md:p-8 space-y-4" data-testid="reviews-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-xl md:text-2xl font-extrabold">Reseñas</h2>
          {canReview.can_review && !showForm && (
            <button onClick={() => setShowForm(true)} data-testid="open-review-form-btn" className="btn-primary px-5 py-2 text-sm">
              <i className="fa-regular fa-star"></i> {myReview ? "Editar mi reseña" : "Dejar reseña"}
            </button>
          )}
        </div>

        {!canReview.can_review && canReview.reason && !myReview && (
          <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-start gap-2" data-testid="cannot-review-reason">
            <i className="fa-solid fa-circle-info text-slate-400 mt-0.5"></i>
            <span>{canReview.reason}</span>
          </div>
        )}

        {showForm && (
          <form onSubmit={submitReview} className="space-y-3 bg-slate-50 border border-slate-200 rounded-2xl p-4" data-testid="review-form">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold">Tu valoración:</span>
              <StarRating value={stars} onChange={setStars} testIdPrefix="review-star" />
            </div>
            <textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              placeholder="¿Qué tal fue tu experiencia? (opcional)"
              className="input-base"
              data-testid="review-comment-input"
            />
            <div className="text-xs text-slate-400 text-right">{comment.length}/500</div>
            {reviewError && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="review-error">{reviewError}</div>}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2 rounded-full border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100" data-testid="cancel-review-btn">
                Cancelar
              </button>
              <button type="submit" disabled={submitting} className="btn-primary px-5 py-2" data-testid="submit-review-btn">
                {submitting ? "Guardando…" : myReview ? "Guardar cambios" : "Publicar reseña"}
              </button>
            </div>
          </form>
        )}

        {reviews.length === 0 ? (
          <div className="text-center py-8 text-slate-500" data-testid="reviews-empty">
            <i className="fa-regular fa-star text-4xl text-slate-300"></i>
            <p className="mt-3 font-semibold">Aún no hay reseñas</p>
            <p className="text-sm">Sé la primera persona en valorar a {profile.name.split(" ")[0]}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reviews.map((r) => {
              const mine = r.author_id === user?.id;
              const initials = (r.author_name || "?").substring(0, 1).toUpperCase();
              return (
                <div key={r.id} className="border border-slate-200 rounded-2xl p-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate flex items-center gap-2">
                          {r.author_name}
                          {mine && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">Tú</span>}
                        </div>
                        <div className="text-xs text-slate-500">{formatDate(r.created_at)}</div>
                      </div>
                    </div>
                    {mine && (
                      <button onClick={deleteOwnReview} data-testid={`delete-review-${r.id}`}
                        className="w-8 h-8 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center" title="Eliminar reseña">
                        <i className="fa-solid fa-trash text-sm"></i>
                      </button>
                    )}
                  </div>
                  <StarRating value={r.stars} readOnly size="text-base" testIdPrefix={`review-stars-${r.id}`} />
                  {r.comment && <p className="text-slate-700 mt-2 whitespace-pre-wrap">{r.comment}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
      <div className="font-semibold mb-2 text-slate-800 flex items-center gap-2"><i className={`fa-solid ${icon} text-slate-500`}></i>{title}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
